import { LitElement, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api, type CodexRun, type KnowledgeUpdateStop } from "../api/client";
import { session } from "../auth/session";
import { i18n } from "../i18n";

const pendingStops = new Set<string>();

export function isKnowledgeUpdate(run: CodexRun): boolean {
  return run.is_knowledge_update ?? Boolean(run.audit?.knowledge_update);
}

export function isExecutionStopped(run: CodexRun): boolean {
  return Boolean(
    run.status === "failed" &&
    run.error &&
    typeof run.error === "object" &&
    run.error.code === "execution_cancelled",
  );
}

export function applyKnowledgeStop(
  run: CodexRun,
  result: KnowledgeUpdateStop,
): CodexRun {
  return {
    ...run,
    status: result.status,
    stop_requested: result.stop_requested,
    stopping: result.stopping,
    ...(result.error_code
      ? { error: { code: result.error_code, message: "" } }
      : {}),
  };
}

@customElement("knowledge-run-stop")
export class KnowledgeRunStop extends LitElement {
  @property({ attribute: false }) run: CodexRun | null = null;
  @state() private busy = false;
  @state() private error = "";
  @state() private acknowledged = false;
  private pendingId = "";

  protected createRenderRoot() {
    return this;
  }

  protected updated(changed: PropertyValues<this>) {
    const previous = changed.get("run") as CodexRun | null | undefined;
    if (changed.has("run") && previous?.id !== this.run?.id) {
      this.error = "";
      this.acknowledged = false;
    }
  }

  private stopping() {
    return this.acknowledged || this.run?.stop_requested || this.run?.stopping;
  }

  private async stop() {
    const run = this.run;
    if (
      !run ||
      !isKnowledgeUpdate(run) ||
      session.user?.role !== "administrator" ||
      !["queued", "running"].includes(run.status) ||
      this.busy ||
      this.stopping() ||
      pendingStops.has(run.id)
    )
      return;
    const id = run.id;
    this.pendingId = id;
    pendingStops.add(id);
    this.busy = true;
    this.error = "";
    try {
      const result = await api.stopKnowledgeUpdate(id);
      if (this.run?.id !== id) return;
      this.acknowledged =
        result.stop_requested && ["queued", "running"].includes(result.status);
      this.dispatchEvent(
        new CustomEvent("stop-state", {
          detail: result,
          bubbles: true,
          composed: true,
        }),
      );
    } catch (error) {
      if (this.run?.id === id) this.error = (error as Error).message;
    } finally {
      pendingStops.delete(id);
      if (this.pendingId === id) {
        this.busy = false;
        this.pendingId = "";
      }
    }
  }

  render() {
    const run = this.run;
    if (
      !run ||
      !isKnowledgeUpdate(run) ||
      !["queued", "running"].includes(run.status) ||
      session.user?.role !== "administrator"
    )
      return nothing;
    return html`<a2k-button
        type="button"
        ?disabled=${this.busy || this.stopping()}
        @click=${() => void this.stop()}
        >${i18n.t(this.busy || this.stopping() ? "knowledgeUpdateStopping" : "knowledgeUpdateStop")}</a2k-button
      >
      ${this.error ? html`<p class="status status-error" role="alert">${i18n.t("knowledgeUpdateStopFailed", { message: this.error })}</p>` : nothing}`;
  }
}
